// src/components/Chatbot.jsx
import DarkMode from '../DarkMode/DarkMode';
import React, { useState, useEffect } from 'react';
import '../CSS/AIChatbot.css';
import chatbotIcon from "../pages/assets/chatbot.png"; // Ensure this path is correct
import closechatbot from "../pages/assets/cross-small.svg"; // Ensure this path is correct
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import searchIndex from '../data/searchIndex'; // Import the search index
import DOMPurify from 'dompurify';
import Fuse from 'fuse.js';
import { debounce } from 'lodash';
import ReactMarkdown from "react-markdown";

const fuse = new Fuse(searchIndex, {
  keys: ["keyword","synonyms"], // Search keywords and synonyms
  threshold: 0.3, // Adjust based on desired sensitivity
});

const Chatbot = ({ isDark, toggleTheme, changeLanguage }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { sender: 'bot', text: `${t('hello')} ${t('how_can_i_help')}` },
  ]);
  const [input, setInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);

  const toggleChatbot = () => setIsOpen(!isOpen);

  const logInteraction = (message, sender) => {
    console.log(`[${new Date().toISOString()}] ${sender}: ${message}`);
    // Optionally, send logs to a backend or analytics service
  };

  const generateResponse = (query) => {
    const results = fuse.search(query);

    if (results.length > 0) {
      let response = "";
      results.forEach((result) => {
        response += `${result.item.response}\n`;
      });
      return response.trim(); // Removes the trailing newline
    } else {
      return t("default_response"); // Default response when no keywords match
    }
  };

  const handleSend = () => {
    if (input.trim() === "") return;

    const userMessage = { sender: "user", text: input };
    setMessages([...messages, userMessage]);
    logInteraction(input, "user");

    const botResponse = generateResponse(input);
    const botMessage = { sender: "bot", text: botResponse };
    setMessages([...messages, userMessage, botMessage]);
    logInteraction(botResponse, "bot");

    setInput("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSend();
    }
  };

  const handleFeedback = (isHelpful) => {
    // Send feedback to analytics or backend for processing
    if (isHelpful) {
      // Positive feedback logic
    } else {
      // Negative feedback logic
    }
  };

  const handleLanguageChange = (lng) => {
    changeLanguage(lng);
    const languageName = getLanguageName(lng);
    const botMessage = { sender: 'bot', text: `${t('language_changed_to')} ${languageName}.` };
    setMessages(prevMessages => [...prevMessages, botMessage]);
  };

  const getLanguageName = (lng) => {
    switch (lng) {
      case 'en':
        return 'English';
      case 'hi':
        return 'Hindi';
      case 'ta':
        return 'Tamil';
      case 'ja':
        return 'Japanese';
      default:
        return 'English';
    }
  };

  const navigateBack = () => {
    // Implement navigation back logic if needed
    navigate(-1); // Goes back to the previous page
  };

  const handleClearChat = () => {
    setMessages([]);
  };

  useEffect(() => {
    const storedHistory = localStorage.getItem("chatHistory");
    if (storedHistory) {
      setMessages(JSON.parse(storedHistory));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("chatHistory", JSON.stringify(messages));
  }, [messages]);

  return (
    <div className={`chatbot-container ${isOpen ? "open" : ""}`}>
      <button
        onClick={toggleChatbot}
        className="chatbot-toggle"
        aria-label={isOpen ? t('close_chatbot') : t('open_chatbot')}
      >
        {isOpen 
          ? <img src={closechatbot} alt={t('close_chatbot')} /> 
          : <img src={chatbotIcon} alt={t('open_chatbot')} />
        }
      </button>
      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <h2>{t('ai_chatbot')}</h2>
            <button className="clear-button" onClick={handleClearChat}>
              {t('clear_chat')}
            </button>
            <div className="controls">
              {/* Dark Mode Toggle */}
              <DarkMode isDark={isDark} toggleTheme={toggleTheme} />

              {/* Language Selector */}
              <select
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="language-select"
                value={i18n.language}
              >
                <option value="en">English</option>
                <option value="hi">Hindi</option>
                <option value="ma">Marwadi</option>
                <option value="ta">Tamil</option>
                <option value="ja">Japanese</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="it">Italian</option>
                <option value="te">Telugu</option>
                <option value="kn">Kannada</option>
                <option value="ml">Malayalam</option>
                <option value="as">Assamese</option>
                <option value="bn">Bengali</option>
                <option value="gu">Gujarati</option>
                <option value="mr">Marathi</option>
                <option value="or">Odia</option>
                <option value="pa">Punjabi</option>
                <option value="ur">Urdu</option>
                <option value="ne">Nepali</option>
              </select>
            </div>
          </div>
          <div className="chatbot-messages">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`chatbot-message ${msg.sender}`}
              >
                {msg.text.split("\n").map((str, idx) => (
                  <span key={idx}>
                    {str}
                    <br />
                  </span>
                ))}
              </div>
            ))}
            <div className="chatbot-navbar">
              <Link to="/" onClick={toggleChatbot}>{t('home')}</Link>
              <Link to="/resume" onClick={toggleChatbot}>{t('resume')}</Link>
              <Link to="/projects" onClick={toggleChatbot}>{t('projects')}</Link>
              <Link to="/contact" onClick={toggleChatbot}>{t('contact')}</Link>
              <Link to="/About-rpx" onClick={toggleChatbot}>{t('About me')}</Link>
            </div>
          </div>
          <div className="chatbot-input">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('ask_anything')}
            />
            <button onClick={handleSend}>{t('send')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;